import { useState, useCallback, useRef } from "react";

export default // ─── Slide-to-Confirm Component ──────────────────────────────
function SlideToConfirm({ label, color, onConfirm }) {
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const firedRef = useRef(false); // Synchronous guard — prevents double-fire
  const trackRef = useCallback((node) => {
    if (node) node._width = node.offsetWidth;
  }, []);
  const trackWidth = 300;
  const thumbWidth = 56;
  const maxDrag = trackWidth - thumbWidth - 4;
  const threshold = maxDrag * 0.6;

  const handleStart = (clientX, track) => {
    setDragging(true);
    const rect = track.getBoundingClientRect();
    track._startX = clientX - rect.left - dragX;
  };

  const handleMove = (clientX, track) => {
    if (!dragging || confirmed) return;
    const rect = track.getBoundingClientRect();
    let x = clientX - rect.left - (track._startX || 0);
    x = Math.max(0, Math.min(maxDrag, x));
    setDragX(x);
  };

  const handleEnd = () => {
    if (!dragging || firedRef.current) return;
    setDragging(false);
    if (dragX >= threshold) {
      firedRef.current = true; // Synchronous — blocks any second call immediately
      setConfirmed(true);
      setDragX(maxDrag);
      try { navigator.vibrate && navigator.vibrate(50); } catch (e) {}
      setTimeout(() => onConfirm(), 300);
    } else {
      setDragX(0);
    }
  };

  return (
    <div
      ref={trackRef}
      style={{
        width: `${trackWidth}px`, height: "52px", borderRadius: "26px",
        background: confirmed ? color : `${color}20`, border: `2px solid ${color}`,
        position: "relative", cursor: dragging ? "grabbing" : "grab",
        overflow: "hidden", userSelect: "none", touchAction: "none",
      }}
      onMouseDown={(e) => handleStart(e.clientX, e.currentTarget)}
      onMouseMove={(e) => handleMove(e.clientX, e.currentTarget)}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchStart={(e) => handleStart(e.touches[0].clientX, e.currentTarget)}
      onTouchMove={(e) => handleMove(e.touches[0].clientX, e.currentTarget)}
      onTouchEnd={handleEnd}
    >
      {/* Label */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "13px", fontWeight: "600", color: confirmed ? "#FFF" : color,
        letterSpacing: "0.3px", pointerEvents: "none",
        opacity: confirmed ? 0 : 1 - (dragX / maxDrag) * 0.5,
      }}>
        {label}
      </div>
      {/* Thumb */}
      <div style={{
        position: "absolute", top: "2px", left: `${2 + dragX}px`,
        width: `${thumbWidth}px`, height: "44px", borderRadius: "22px",
        background: color, display: "flex", alignItems: "center", justifyContent: "center",
        transition: dragging ? "none" : "left 0.2s ease",
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
      }}>
        <span style={{ color: "#FFF", fontSize: "18px", fontWeight: "700" }}>›››</span>
      </div>
      {/* Confirmed check */}
      {confirmed && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: "#FFF", fontSize: "18px", fontWeight: "700" }}>✓</span>
        </div>
      )}
    </div>
  );
}

// ─── Treatment Session Screen ────────────────────────────────

